import { Svg, Path } from '@react-pdf/renderer'
import { Filesystem, Directory } from '@capacitor/filesystem'
import { Share } from '@capacitor/share'
import { isNative } from '@/lib/native-file-viewer'
import pronteerLogoSvg from '@/assets/pronteer-logo-full.svg?raw'

interface ParsedSvgPath {
  d: string
  fill: string
  fillRule?: 'nonzero' | 'evenodd'
}

interface ParsedSvg {
  viewBox: string
  width: number
  height: number
  paths: ParsedSvgPath[]
}

function parseSvgSource(svg: string): ParsedSvg {
  const viewBox = /viewBox="([^"]+)"/.exec(svg)?.[1] ?? '0 0 100 100'
  const [, , vw, vh] = viewBox.split(/\s+/).map(Number)

  const paths: ParsedSvgPath[] = []
  const pathTagRegex = /<path\b([^>]*?)\/?>/g
  let match: RegExpExecArray | null
  while ((match = pathTagRegex.exec(svg)) !== null) {
    const attrs = match[1]
    const d = /\sd="([^"]+)"/.exec(attrs)?.[1]
    if (!d) continue
    const fill = /\sfill="([^"]+)"/.exec(attrs)?.[1] ?? '#000'
    const fillRuleRaw = /\sfill-rule="([^"]+)"/.exec(attrs)?.[1]
    const fillRule =
      fillRuleRaw === 'evenodd' || fillRuleRaw === 'nonzero' ? fillRuleRaw : undefined
    paths.push({ d, fill, fillRule })
  }

  return { viewBox, width: vw || 100, height: vh || 100, paths }
}

const PRONTEER_LOGO = parseSvgSource(pronteerLogoSvg)

export function PronteerLogo({ width = 96 }: { width?: number }) {
  const height = (width * PRONTEER_LOGO.height) / PRONTEER_LOGO.width
  return (
    <Svg width={width} height={height} viewBox={PRONTEER_LOGO.viewBox}>
      {PRONTEER_LOGO.paths.map((p, i) => (
        <Path
          key={i}
          d={p.d}
          fill={p.fill}
          {...(p.fillRule ? { fillRule: p.fillRule } : {})}
        />
      ))}
    </Svg>
  )
}

export async function fetchAsDataUrl(url: string): Promise<string | null> {
  try {
    const res = await fetch(url)
    if (!res.ok) return null
    const blob = await res.blob()
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  } catch {
    return null
  }
}

export async function blobToBase64(blob: Blob): Promise<string> {
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const dataUrl = reader.result as string
      resolve(dataUrl.split(',')[1] ?? '')
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

export function sanitizeFilename(name: string, fallback = 'document'): string {
  return name.replace(/[^a-z0-9-_]+/gi, '_').toLowerCase() || fallback
}

/**
 * Hand a generated PDF off: native share sheet on iOS/Android, file download
 * on web.
 */
export async function deliverPdf(
  blob: Blob,
  filename: string,
  shareTitle: string,
): Promise<void> {
  if (isNative) {
    const base64 = await blobToBase64(blob)
    const written = await Filesystem.writeFile({
      path: filename,
      data: base64,
      directory: Directory.Cache,
    })
    await Share.share({
      title: shareTitle,
      url: written.uri,
      dialogTitle: 'Share PDF',
    })
    return
  }

  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}
